import { Box, Grid, Typography } from '@material-ui/core';
import { Button, ConfirmDialog, InformationDialog, Tabs, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { ReactNode, useEffect, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useGeneralTasksStore } from '../../hooks/useGeneralTasksStore';
import { ChatSection, FilterType, useLoanCommunicationStore } from '@roc/feature-communication';
import { ChatContainer } from '@chatscope/chat-ui-kit-react';
import { ForumOutlined } from '@material-ui/icons';
import { insertIf, isNil, isNotBlank, LoanProcess } from '@roc/feature-utils';
import { UwComments } from './uwComments';
import { Alert } from '@material-ui/lab';
import { TASK_IN_PROGRESS_MESSAGE } from '../../utils/generalTasksConstants';
import { areActionsUnavailableBasedOnTask } from '../../utils/generalTasksUtils';
import { TaskStatus, TaskV2, TaskV2Type } from '../../types/generalTasksTypes';
import { DocumentName, useDocumentStore } from '@roc/feature-documents';
import { Document } from '@roc/feature-types';
import { useLoanStore } from '@roc/feature-loans';
import { AssignToDialog } from '../assignTo/assignToDialog';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      marginTop: '-45px',
      height: '80vh',
    },
    autoHeightContainer: {
      width: '100%',
      marginTop: '-45px',
    },
    tabWrapper: {
      marginLeft: '-32px',
      paddingLeft: '0px !important',
      paddingTop: '0px !important',
    },
    alertWrapper: {
      padding: '4px',
      paddingLeft: '0px !important',
      marginLeft: '-10px',
    },
    tab: {
      borderRight: 'none !important',
      borderTop: 'none !important',
      borderLeft: 'none !important',
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      '& .MuiTab-root': {
        maxWidth: '100% !important',
      },
    },
    createChatButton: {
      letterSpacing: 'normal',
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: '16px',
      backgroundColor: '#4285F4',
      width: '307px',
      height: '64px',
      padding: '8px 16px',
      boxShadow: '0px 4px 4px 0px #00000040',
      '&:hover': {
        backgroundColor: '#357AE8',
      },
    },
    createChatButtonContainer: {
      height: '68vh',
      border: '1px solid #CBCBCB',
      borderRadius: '10px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    circleIconContainer: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '5px',
    },
    informationDialogText: {
      margin: '-32px',
    },
  })
);


interface CustomTaskProps {
  title: string;
  taskId: number;
  customContent: ReactNode;
  selectedTabIndex: number;
  setSelectedTabIndex: (i: number) => void;
  allowVertScroll?: boolean;
  readOnly?: boolean;
}

const TAB_CHAT = 'CHAT';
const TAB_UW_NOTES = 'NOTES';

export const CustomTask = observer(({ title, taskId, customContent, selectedTabIndex, setSelectedTabIndex, allowVertScroll = true, readOnly = false }: CustomTaskProps) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const areTaskActionsUnavailable = areActionsUnavailableBasedOnTask(editTaskStore.taskV2);
  const [confirmModalAction, setConfirmModalAction] = useState<boolean>(false);
  const [showAssignToModal, setShowAssignToModal] = useState<TaskV2 | undefined>(undefined);
  const [informationModalText, setInformationModalText] = useState('');
  const { documentStore } = useDocumentStore();
  const { loanStore } = useLoanStore();
  const isInternalPortal = generalTasksStore.globalStore.isInternalPortal;

  const classes = useStyles();
  const tabs = [
    ...insertIf(!isNil(customContent), [
      { label: title }
    ]),
    ...insertIf(!generalTasksStore.globalStore.isBorrowerPortal, [{ label: TAB_CHAT }]),
    ...insertIf(generalTasksStore.globalStore.isInternalPortal, [{ label: TAB_UW_NOTES }])
  ];

  useEffect(() => {
    editTaskStore.reloadTaskById(taskId, true);
  }, []);

  useEffect(() => {
    generalTasksStore.checkIfTaskRoomExist(taskId, () => loadChat(), undefined, true)
  }, [selectedTabIndex])

  const loadChat = () => {
    setShowLoading(true)
    loanCommunicationStore.reset();
    loanCommunicationStore.setFilter({
      type: FilterType.TASK_ID,
      value: taskId
    });
    if (loanCommunicationStore.isInternal) {
      loanCommunicationStore.getAllMessageSnippets();
    }
    loanCommunicationStore.loadMyConversations(1, 100, (totalCount, rows) => {
      setShowLoading(false);
      if (rows[0]) {
        loanCommunicationStore.setCurrentConversation(loanCommunicationStore.conversations[rows[0].conversationSid])
      }
    }, () => {
      setShowLoading(false);
    });
  }

  const createChat = (taskId) => {
    generalTasksStore.createTaskChatroom(taskId, () => {
      loadChat()
    }, true);
  }

  const onTabChange = (e, index) => {
    setSelectedTabIndex(index);
  }

  const chatTabContent = () => {
    const showParentTaskBanner = editTaskStore.taskV2?.parentTask && generalTasksStore.globalStore.isInternalPortal;
    return (
      <Grid item xs={12} style={{ padding: 16 }}>
        {showParentTaskBanner && (
          <Alert severity="info" style={{ marginBottom: 12 }}>
            This is a chat task. Please reply to the messages below or like the last message in the conversation to remove it from your queue.
          </Alert>
        )}
        {generalTasksStore.taskRoomExist ?
          <Box height={'75vh'} style={{
            border: '1px solid #CBCBCB',
            borderRadius: '10px',
            padding: '8px',
          }}>
            <ChatSection
              as={ChatContainer}
              communicationStore={loanCommunicationStore}
              allowJoinConversation={true}
              allowLeaveConversation={true}
              showSnippet={false}
              showEmailAddress={false}
              allowManageParticipants={true}
              allowAddInternalParticipants={true}
              allowAddParticipants={true}
              allowRemoveParticipants={true}
              onDownloadConversation={undefined}
              onUnarchiveConversation={() => { }}
              showLoading={showLoading}
            />
          </Box>
          :
          <Box className={classes.createChatButtonContainer}>
            <Button
              testId="downloadAll"
              onClick={() => createChat(taskId)}
              variant="contained"
              className={classes.createChatButton}
            >
              <Box display="flex" alignItems="center">
                <Box className={classes.circleIconContainer}>
                  <ForumOutlined style={{ color: '#4285F4' }}></ForumOutlined>
                </Box>
                <Typography variant="body1" style={{ fontSize: '16px', fontWeight: 'bold', marginRight: '8px', color: '#FFFFFF' }}>
                  Start Conversation
                </Typography>
              </Box>
            </Button>
            <Typography variant="body1" style={{ marginTop: '5px', fontSize: '12px', fontWeight: 'normal' }}>
              Contact us with any questions about this document.
            </Typography>
          </Box>
        }
      </Grid>
    );
  }

  const personalNotesContent = () => {
    return (
      <Grid item xs={12} >
        <UwComments taskId={taskId} />
      </Grid>
    );
  }

  const fetchTodos = (loanId: number, loanProcess: LoanProcess) => {
    documentStore.loanTodosStore.fetchTodosForMultipleLoanProcesses(loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING], true);
  }

  const handleOpenConfirmModal = () => {
    if (areTaskActionsUnavailable) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else {
      setConfirmModalAction(true);
    }
  }

  const handleClickAssignTo = () => {
    if (areTaskActionsUnavailable) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else {
      const task = editTaskStore.taskV2;
      setShowAssignToModal(task);
    }
  }

  const handleCloseAssignToModal = () => {
    loanStore.fetchLoanDetails(showAssignToModal?.loanId, () => { }, true);
    fetchTodos(showAssignToModal?.loanId!, LoanProcess.UNDERWRITING);
    setShowAssignToModal(undefined);
  }

  const handleSubmitConfirmModal = () => {
    const task = editTaskStore.taskV2;

    if (confirmModalAction && task) {
      documentStore.loanTodosStore.updateTodoStatusV2(
        { taskId } as unknown as Document,
        TaskStatus.ACCEPTED,
        () => fetchTodos(task.loanId!, LoanProcess.UNDERWRITING)
      );
    }
    setConfirmModalAction(false);
  };

  const isParentTask = !!(editTaskStore.taskV2 as any)?.parentTask;

  const readOnlyOptions = () => <>
    {isInternalPortal && !isParentTask && (
      <Grid item xs={12} md={12}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" gridGap="1rem" mr="16px" mb="16px">
          <Button variant="contained" color="primary" testId='assign-to' onClick={handleClickAssignTo}>
            Assign to
          </Button>
          <Button variant="contained" color="primary" testId='masrk-as-completed' onClick={handleOpenConfirmModal}
          >
            Mark as complete
          </Button>
        </Box>
      </Grid>
    )}
    <ConfirmDialog
      handleConfirm={handleSubmitConfirmModal}
      handleClose={() => setConfirmModalAction(false)}
      handleCancel={() => setConfirmModalAction(false)}
      open={confirmModalAction}
      body={<Typography>Please confirm you want to mark the task as completed.</Typography>}
      confirmButtonText="Confirm"
      cancelButtonText="Close"
      title={'Confirmation'}
    />
    <AssignToDialog
      open={showAssignToModal !== undefined}
      loanId={showAssignToModal?.loanId!}
      taskId={showAssignToModal?.taskId!}
      taskName={showAssignToModal?.taskCode as DocumentName}
      readOnly={showAssignToModal?.readOnly}
      propertyId={showAssignToModal?.typeId2}
      handleClose={handleCloseAssignToModal}
    />
    <InformationDialog
      open={informationModalText !== ''}
      title="Action Unavailable"
      handleClose={() => setInformationModalText('')}
      body={<Typography className={classes.informationDialogText}>{informationModalText}</Typography>}
    />
  </>

  return (
    <Box className={allowVertScroll ? classes.container: classes.autoHeightContainer}>
      <Grid container spacing={4} style={{ paddingTop: 0 }}>
        {areTaskActionsUnavailable && (
          <Grid item xs={12} md={12} className={classes.alertWrapper}>
            <Alert severity="warning">{TASK_IN_PROGRESS_MESSAGE}</Alert>
          </Grid>
        )}
        <Grid item xs={12} md={12} className={classes.tabWrapper}>
          <Tabs
            tabs={tabs}
            selectedTab={selectedTabIndex}
            onTabChange={onTabChange}
            className={classes.tab}
            variant={''}
          />
        </Grid>
        {(readOnly || editTaskStore.taskV2?.readOnly) && readOnlyOptions()}
        {tabs[selectedTabIndex].label === title && customContent}
        {tabs[selectedTabIndex].label === TAB_CHAT && chatTabContent()}
        {tabs[selectedTabIndex].label === TAB_UW_NOTES &&
          personalNotesContent()}
      </Grid>
    </Box>
  );
});
